import React, { useState, useCallback } from 'react';
import { 
  Container, 
  Paper, 
  Title, 
  Text, 
  Group, 
  Button, 
  Table,
  ScrollArea,
  Stack,
  Center,
  Loader,
  Badge,
  ActionIcon,
  Tooltip,
  Box
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconUpload, IconX, IconFile, IconCopy, IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

const HillyP = () => {
  const [files, setFiles] = useState([]);
  const [extractedData, setExtractedData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [copied, setCopied] = useState(false);
  const [copiedRows, setCopiedRows] = useState({});

  const extractTextFromPDF = async (file) => {
    const pdfjsLib = window.pdfjsLib;
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;
    
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      // Preserve line breaks better
      let lastY = null;
      let pageText = '';
      
      textContent.items.forEach(item => {
        if (lastY !== null && Math.abs(lastY - item.transform[5]) > 5) {
          pageText += '\n';
        }
        pageText += item.str + ' ';
        lastY = item.transform[5];
      });
      
      fullText += pageText + '\n\n';
    }
    
    return fullText;
  };

  const parseCheckInForm = (text, fileName) => {
    const checkInData = {
      fileName: fileName,
      clientName: '',
      date: '',
      responses: {}
    };
    
    console.log("Full extracted text:", text); // Debug log to see what we're working with
    
    // Extract client name - it appears between "Weekly Client Check-In Form" and the date/time
    const headerMatch = text.match(/Weekly Client Check-In Form\s+([A-Za-z\s]+?)\s+\d{1,2}\/\d{1,2}\/\d{2,4}/i);
    if (headerMatch) {
      checkInData.clientName = headerMatch[1].trim();
    } else {
      // Fallback to finding any name pattern
      const nameMatch = text.match(/([A-Z][a-z]+\s+[A-Z][a-z]+)/);
      if (nameMatch) {
        checkInData.clientName = nameMatch[1].trim();
      }
    }
    
    // Extract date 
    const dateMatch = text.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/);
    if (dateMatch) {
      checkInData.date = dateMatch[1].trim();
    }
    
    // Define full questions exactly as they appear in the form
    const questions = [
      "1. What's one NON-SCALE WIN that you've noticed concerning your body this week?",
      "2. What are one or two WINS that you're proud of this week?",
      "3. Looking back at your short-term goal from last week, did you achieve it? What did you learn from the process—what worked, what didn't, and what will you take into this week?",
      "4. What phase of your cycle are you in right now?",
      "5. Have you noticed any changes in your period or HA recovery this week? (Consider flow, bleeding pattern, duration, cycle length, cervical mucus, cramping, mood changes, or other symptoms)",
      "6. How much water did you drink per day on average, and how well did you feel hydrated?",
      "7. How would you describe your caffeine intake this past week?",
      "8. How would you describe your bowel movement frequency this past week?",
      "9. What was your additional activity outside of your exercise program this week? (Include step count, extra workouts, or other physical activity)",
      "10. How would you rate your overall energy levels this week?",
      "11. How many hours of sleep did you average per night?",
      "12. How would you describe your sleep QUALITY this past week?",
      "13. How would you describe your overall stress levels this week? If your stress was high, what do you think contributed most to it?",
      "14. How have you been feeling in your body this week?",
      "15. Think about your activity this week. Did any sessions, exercises, or movements feel particularly good or frustrating? Please explain why.",
      "16. Have you noticed any NEW aches or pains, unusual fatigue, digestive changes, or other physical symptoms this week? If yes, please describe:",
      "17. How would you describe your approach to tracking macros this past week?",
      "18. Rate your hunger this week",
      "19. How in tune with your HUNGER AND FULLNESS CUES were you this week?",
      "20. On a scale of 1–5 (1 being not at all connected, 5 being very connected), how CONNECTED do you feel with your body this week? Why?",
      "21. How has your RELATIONSHIP with food been this week?",
      "22. On a scale of 1–5, how well do you feel you followed your training, nutrition, and recovery protocols this week?",
      "23. Moving into next week, what is a current short-term goal you have for yourself this week, and how are you setting yourself up to achieve it?",
      "24. Is there a specific area in your plan or coaching that you'd like more guidance, support, or education in right now?",
      "25. Is there anything coming up next week (or in the coming weeks) I should know about, or any additional thoughts, reflections, or feedback you'd like to share?",
      "26. OPTIONAL Short Journal Reflection"
    ];
    
    // Initialize all questions with empty responses to ensure they all appear
    for (const q of questions) {
      checkInData.responses[q] = '';
    }
    
    // For each question, find it in the text and extract everything until the next question
    for (let i = 0; i < questions.length; i++) {
      const currentQ = questions[i];
      const questionNum = i + 1;
      
      // Find this question number in the text
      const questionPattern = new RegExp(`${questionNum}\\.\\s*`, 'g');
      const questionMatches = [...text.matchAll(questionPattern)];
      
      if (questionMatches.length > 0) {
        const questionIndex = questionMatches[0].index;
        
        // Find where the next question starts
        let endIndex = text.length;
        if (i < questions.length - 1) {
          const nextNum = i + 2;
          const nextPattern = new RegExp(`${nextNum}\\.\\s*`, 'g');
          const nextMatches = [...text.matchAll(nextPattern)];
          if (nextMatches.length > 0 && nextMatches[0].index > questionIndex) {
            endIndex = nextMatches[0].index;
          }
        } else if (questionNum === 26) {
          // For the last question, look for the closing message
          const closingPattern = /Thank you for completing your check-in.*?Have the best day.*?!!!/i;
          const closingMatch = text.match(closingPattern);
          if (closingMatch) {
            endIndex = text.indexOf(closingMatch[0]);
          }
        }
        
        // Extract the section for this question
        let sectionText = text.substring(questionIndex, endIndex);
        
        // Get the full question text without the number
        const fullQuestionText = currentQ.replace(/^\d+\.\s*/, '');
        
        // Find where the question text actually ends in the section
        // We'll look for the entire question text and skip past it
        let answerStartPos = 0;
        
        // Try to find the full question text in the section
        const escapedQuestion = fullQuestionText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const questionRegex = new RegExp(questionNum + '\\.\\s*' + escapedQuestion, 'is');
        const questionMatch = sectionText.match(questionRegex);
        
        if (questionMatch) {
          answerStartPos = questionMatch[0].length;
        } else {
          // If we can't find the full question, look for key parts
          const lines = sectionText.split('\n');
          let foundEnd = false;
          
          for (let j = 0; j < lines.length && j < 8; j++) {
            const lineText = lines.slice(0, j + 1).join(' ');
            
            // For question 3, look for the specific ending
            if (questionNum === 3 && lineText.includes('take into this week?')) {
              answerStartPos = sectionText.indexOf('take into this week?') + 'take into this week?'.length;
              foundEnd = true;
              break;
            }
            
            // For questions with parentheses, look for the closing parenthesis
            if (fullQuestionText.includes('(') && lineText.includes(')')) {
              const parenEnd = lineText.lastIndexOf(')');
              answerStartPos = lineText.substring(0, parenEnd + 1).length;
              foundEnd = true;
              break;
            }
            
            // For question 18, look for the number sequence
            if (questionNum === 18 && lineText.includes('012345')) {
              answerStartPos = lineText.indexOf('012345') + '012345'.length;
              foundEnd = true;
              break;
            }
            
            // For question 20, look for "Why?"
            if (questionNum === 20 && lineText.includes('Why?')) {
              answerStartPos = lineText.indexOf('Why?') + 'Why?'.length;
              foundEnd = true;
              break;
            }
            
            // For other questions, look for the question mark
            if (lineText.includes('?') && !lineText.includes('?', lineText.indexOf('?') + 1)) {
              answerStartPos = lineText.indexOf('?') + 1;
              foundEnd = true;
              break;
            }
          }
          
          // If still not found, just skip the first few lines
          if (!foundEnd) {
            const firstLines = lines.slice(0, 3).join('\n');
            answerStartPos = firstLines.length;
          }
        }
        
        // Extract everything after the question as the answer
        let answerText = sectionText.substring(answerStartPos).trim();
        
        // Process the answer text to clean it up
        const answerLines = answerText.split('\n');
        let cleanedLines = [];
        const isRatingQuestion = currentQ.includes('Rate') || currentQ.includes('scale of 1–5');
        
        for (let j = 0; j < answerLines.length; j++) {
          const line = answerLines[j].trim();
          
          // Skip empty lines, page markers, headers, and rating instructions
          if (!line || 
              line.match(/^Page \d+/) || 
              line.includes('Weekly Client Check-In Form') ||
              line.match(/^\d+\s*\/\s*\d+$/) || // Page numbers like "1 / 5"
              line.match(/[A-Z][a-z]+\s+[A-Z][a-z]+.*\d{1,2}\/\d{1,2}\/\d{4}/) || // Any name with date
              line.match(/^\d+\s*\/\s*\d+\s+[A-Z]/) || // Page number followed by name
              line.includes('AM') && line.includes('/') || // Timestamps
              line.includes('PM') && line.includes('/') || // Timestamps
              line === '0: Not at all 5: On point!' ||
              line === '0 : Not at all 5 : On point!' ||
              (line === '0 1 2 3 4 5' && !isRatingQuestion) || // Keep if it's a rating answer
              line === '012345') {
            continue;
          }
          
          // For rating questions, check if this line is a number selection or rating
          if (isRatingQuestion) {
            // Check for single digit rating (0-5)
            if (line.match(/^\d$/) || line.match(/^\d\s*\/\s*5$/)) {
              cleanedLines.push(line.includes('/') ? line : line + '/5');
              continue;
            }
            // Check for circled or selected number in a range
            if (line.match(/0\s+1\s+2\s+3\s+4\s+5/)) {
              // This might be the rating scale itself - check if there's a selection after it
              continue;
            }
          }
          
          // Check if this line has multiple bullets on the same line
          if (line.includes('•')) {
            // Check if there are multiple bullets (2 or more • characters)
            const bulletCount = (line.match(/•/g) || []).length;
            if (bulletCount >= 2) {
              // Multiple bullets on same line - split them
              const parts = line.split('•');
              let isFirst = true;
              for (let part of parts) {
                const trimmed = part.trim();
                if (trimmed) {
                  const bulletLine = '• ' + trimmed;
                  if (isFirst && cleanedLines.length === 0) {
                    cleanedLines.push(bulletLine);
                    isFirst = false;
                  } else {
                    cleanedLines.push('\n' + bulletLine);
                  }
                }
              }
              continue;
            }
          }
          
          // Preserve single bullet points and formatting
          if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
            // This is a bullet point - ALWAYS add it on a new line
            if (cleanedLines.length > 0) {
              cleanedLines.push('\n' + line);
            } else {
              cleanedLines.push(line);
            }
          } else {
            cleanedLines.push(line);
          }
        }
        
        // Join answer lines - DON'T add spaces between lines with bullets
        let answer = '';
        for (let i = 0; i < cleanedLines.length; i++) {
          const line = cleanedLines[i];
          if (i === 0) {
            answer = line;
          } else if (line.startsWith('\n')) {
            // Line already has line break (bullet points)
            answer += line;
          } else {
            // Regular text needs a space
            answer += ' ' + line;
          }
        }
        answer = answer.trim();
        
        // Aggressive cleanup of question text fragments and names
        // First, extract the client name to remove it wherever it appears
        const clientNameMatch = text.match(/Weekly Client Check-In Form\s+([A-Za-z\s]+?)\s+\d{1,2}\/\d{1,2}\/\d{4}/i);
        const clientName = clientNameMatch ? clientNameMatch[1].trim() : null;
        
        // Remove specific question fragments based on question number
        if (questionNum === 3) {
          answer = answer
            .replace(/What did\s*you learn from the process[—-]*\s*/gi, '')
            .replace(/what worked, what didn't, and what will you take into this week\??\s*/gi, '')
            .replace(/what worked, what didn't.*?week\??\s*/gi, '')
            .replace(/what worked.*?this week\??\s*/gi, '');
        } else if (questionNum === 5) {
          answer = answer
            .replace(/\(\s*Consider flow.*?symptoms\s*\)\s*/gi, '')
            .replace(/\(\s*Consider.*?\)\s*/gi, '');
        } else if (questionNum === 9) {
          answer = answer
            .replace(/\(\s*Include step count.*?physical activity\s*\)\s*/gi, '')
            .replace(/\(\s*Include.*?\)\s*/gi, '');
        } else if (questionNum === 13) {
          answer = answer
            .replace(/If your stress was\s*high.*?to it\??\s*/gi, '')
            .replace(/^\?\s*/, ''); // Remove lone question mark
        } else if (questionNum === 15) {
          answer = answer
            .replace(/Please explain why\.?\s*/gi, '')
            .replace(/Did any sessions.*?frustrating\??\s*/gi, '');
        } else if (questionNum === 16) {
          answer = answer
            .replace(/If yes, please describe:?\s*/gi, '');
        } else if (questionNum === 18) {
          // For hunger rating, extract just the number
          const ratingMatch = answer.match(/\b([0-5])\b/);
          if (ratingMatch) {
            answer = ratingMatch[1];
          }
        } else if (questionNum === 20) {
          // The answer should just be a number between the question and the next one
          // Only remove the most obvious question fragments if they appear
          if (answer.includes('how CONNECTED')) {
            const parts = answer.split('Why?');
            if (parts.length > 1) {
              answer = parts[1].trim();
            }
          }
          
          // Look for a number 0-5
          const match = answer.match(/\b([0-5])\b/);
          if (match) {
            answer = match[1];
          }
        } else if (questionNum === 22) {
          // First look for the rating number before removing scale text
          const originalAnswer = answer;
          const ratingMatch = originalAnswer.match(/\b([0-5])\b(?!.*:)/); // Find a number not followed by colon
          
          // Remove the rating scale text
          answer = answer
            .replace(/0\s*:\s*Not at all\s+5\s*:\s*On point!?\s*/gi, '')
            .replace(/^\s*0\s+1\s+2\s+3\s+4\s+5\s*/, '');
          
          // If we found a rating, use just the number
          if (ratingMatch && ratingMatch[1] !== '0' && ratingMatch[1] !== '5') {
            answer = ratingMatch[1];
          } else if (answer.trim()) {
            // If there's other text, keep it
            answer = answer.trim();
          }
        } else if (questionNum === 25) {
          answer = answer
            .replace(/I should know\s*about,?\s*/gi, '')
            .replace(/or any additional.*?share\??\s*/gi, '');
        } else if (questionNum === 26) {
          // Remove the closing message if it appears
          answer = answer
            .replace(/Thank you for completing.*?life!+\s*/gi, '')
            .replace(/Have the best day.*?!+\s*/gi, '');
          // If only whitespace remains, clear the answer
          if (!answer.trim()) {
            answer = '';
          }
        }
        
        // Remove client name wherever it appears (before and after other cleanups)
        if (clientName) {
          const nameRegex = new RegExp(clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
          answer = answer.replace(nameRegex, '').replace(/[ ]{2,}/g, ' ').trim();
        }
        
        // Remove any lingering question text
        const questionPhrases = [
          'What did you learn from the process',
          'what worked, what didn\'t',
          'Consider flow, bleeding',
          'Include step count',
          'Please explain why',
          'If yes, please describe',
          'how CONNECTED do you feel',
          'I should know about',
          'or any additional thoughts'
        ];
        
        for (const phrase of questionPhrases) {
          const phraseRegex = new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '.*?\\?', 'gi');
          answer = answer.replace(phraseRegex, '');
        }
        
        // Final cleanup - BE CAREFUL NOT TO REMOVE LINE BREAKS
        answer = answer
          .replace(/\d+\s*\/\s*\d+\s+[A-Z][a-z]+\s+[A-Z][a-z]+.*?(AM|PM)/g, '') // Remove timestamps
          .replace(/^\d+\s*\/\s*\d+\s*/gm, '') // Remove page numbers
          .replace(/\s+0\s*:\s*Not at all\s+5\s*:\s*On point!/gi, '') // Remove rating scale
          .replace(/^\s*\?\s*/, '') // Remove standalone question marks
          .replace(/[ ]{2,}/g, ' ') // Normalize SPACES only, not line breaks
          .trim();
        
        // One final pass to remove client name
        if (clientName) {
          answer = answer.replace(new RegExp(clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '').trim();
        }
        
        // If the answer is a single line with a bullet point, remove the bullet
        if (answer && !answer.includes('\n')) {
          // Single line answer - check if it starts with a bullet
          if (answer.match(/^[•\-\*]\s+/)) {
            answer = answer.replace(/^[•\-\*]\s+/, '');
          }
        }
        
        // Store the answer if we found one (overwrites the empty initialization)
        if (answer && answer.length > 0) {
          checkInData.responses[currentQ] = answer;
        }
        // Empty answers stay as '' from initialization
      }
    }
    
    return checkInData;
  };

  const handleDrop = useCallback(async (acceptedFiles) => {
    setIsProcessing(true);
    const newData = [];
    const allQuestions = new Set();
    
    try {
      for (const file of acceptedFiles) {
        const text = await extractTextFromPDF(file);
        const parsed = parseCheckInForm(text, file.name);
        newData.push(parsed);
        Object.keys(parsed.responses).forEach(q => allQuestions.add(q));
      }
      
      setFiles(prev => [...prev, ...acceptedFiles]);
      setExtractedData(prev => [...prev, ...newData]);
      setQuestions(Array.from(allQuestions).sort());
      
      notifications.show({
        title: 'Success',
        message: `Processed ${acceptedFiles.length} PDF(s) successfully`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to process PDF files',
        color: 'red',
      });
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const copyTableToClipboard = async () => {
    if (extractedData.length === 0) return;
    
    // Don't include headers, just copy the data
    let tableText = '';
    
    extractedData.forEach(data => {
      const row = [data.clientName || 'Unknown'];
      questions.forEach(q => {
        row.push(data.responses[q] || '');
      });
      tableText += row.join('\t') + '\n';
    });
    
    try {
      await navigator.clipboard.writeText(tableText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      notifications.show({
        title: 'Copied',
        message: 'Table copied to clipboard - paste into Notion or Excel',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to copy to clipboard',
        color: 'red',
      });
    }
  };

  const copyRowToClipboard = async (rowIndex) => {
    const data = extractedData[rowIndex];
    if (!data) return;
    
    const row = [data.clientName || 'Unknown'];
    questions.forEach(q => {
      row.push(data.responses[q] || '');
    });
    
    try {
      await navigator.clipboard.writeText(row.join('\t'));
      setCopiedRows({ ...copiedRows, [rowIndex]: true });
      setTimeout(() => {
        setCopiedRows(prev => ({ ...prev, [rowIndex]: false }));
      }, 2000);
      notifications.show({
        title: 'Row Copied',
        message: 'Row copied to clipboard',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to copy row',
        color: 'red',
      });
    }
  };

  const clearData = () => {
    setFiles([]);
    setExtractedData([]);
    setQuestions([]);
  };

  return (
    <Box py="xl">
      <Container size="xl">
        <Stack spacing="lg">
        <div>
          <Title order={1}>PDF Check-In Extractor</Title>
          <Text color="dimmed" size="lg" mt="xs">
            Upload weekly check-in PDFs to extract and organize responses
          </Text>
        </div>

        <Paper shadow="sm" p="md" withBorder>
          <Dropzone
            onDrop={handleDrop}
            onReject={() => {
              notifications.show({
                title: 'Error',
                message: 'Only PDF files are accepted',
                color: 'red',
              });
            }}
            maxSize={10 * 1024 ** 2}
            accept={['application/pdf']}
            loading={isProcessing}
          >
            <Group position="center" spacing="xl" style={{ minHeight: 220, pointerEvents: 'none' }}>
              <Dropzone.Accept>
                <IconUpload size={50} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={50} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconFile size={50} stroke={1.5} />
              </Dropzone.Idle>

              <div>
                <Text size="xl" inline>
                  Drag PDF files here or click to select
                </Text>
                <Text size="sm" color="dimmed" inline mt={7}>
                  Upload one or multiple check-in PDFs (max 10MB each)
                </Text>
              </div>
            </Group>
          </Dropzone>

          {files.length > 0 && (
            <Group mt="md">
              <Text size="sm" weight={500}>Files uploaded:</Text>
              {files.map((file, index) => (
                <Badge key={index} variant="filled">
                  {file.name}
                </Badge>
              ))}
            </Group>
          )}
        </Paper>

        {isProcessing && (
          <Center>
            <Loader size="lg" />
          </Center>
        )}

        </Stack>
      </Container>
      
      {extractedData.length > 0 && !isProcessing && (
        <Box mt="xl">
          <Container size="xl" mb="md">
            <Group position="apart">
              <Text size="lg" weight={500}>
                Extracted Data ({extractedData.length} check-ins)
              </Text>
              <Group>
                <Tooltip label="Copy all rows (no headers)">
                  <Button 
                    leftIcon={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    variant="light"
                    color={copied ? "green" : "blue"}
                    onClick={copyTableToClipboard}
                  >
                    {copied ? "Copied!" : "Copy All"}
                  </Button>
                </Tooltip>
                <Button 
                  leftIcon={<IconX size={16} />}
                  color="red"
                  variant="light"
                  onClick={clearData}
                >
                  Clear
                </Button>
              </Group>
            </Group>
          </Container>

          <Box style={{ width: '100%', overflowX: 'auto' }}>
            <Paper shadow="sm" p="md" withBorder>
              <Table striped highlightOnHover>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>
                      Copy
                    </th>
                    <th style={{ minWidth: 200, position: 'sticky', left: 50, background: 'white', zIndex: 1 }}>
                      Client Name
                    </th>
                      {questions.sort((a, b) => {
                        const numA = parseInt(a.match(/^(\d+)\./)?.[1] || 0);
                        const numB = parseInt(b.match(/^(\d+)\./)?.[1] || 0);
                        return numA - numB;
                      }).map(q => (
                        <th key={q} style={{ minWidth: 400, maxWidth: 600 }}>
                          <Text size="xs" weight={500}>
                            {q}
                          </Text>
                        </th>
                      ))}
                    </tr>
                  </thead>
                <tbody>
                  {extractedData.map((data, index) => (
                    <tr key={index}>
                      <td>
                        <Tooltip label={copiedRows[index] ? "Copied!" : "Copy row"}>
                          <ActionIcon 
                            variant="subtle"
                            color={copiedRows[index] ? "green" : "blue"}
                            onClick={() => copyRowToClipboard(index)}
                          >
                            {copiedRows[index] ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      </td>
                      <td style={{ position: 'sticky', left: 50, background: 'white', zIndex: 1 }}>
                        <Text size="sm" weight={600}>
                          {data.clientName || 'Unknown'}
                        </Text>
                      </td>
                        {questions.sort((a, b) => {
                          const numA = parseInt(a.match(/^(\d+)\./)?.[1] || 0);
                          const numB = parseInt(b.match(/^(\d+)\./)?.[1] || 0);
                          return numA - numB;
                        }).map(q => (
                          <td key={q} style={{ minWidth: 400, maxWidth: 600 }}>
                            <Text 
                              size="sm" 
                              style={{ 
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}
                            >
                              {data.responses[q] || '-'}
                            </Text>
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default HillyP;